import { loadSettings, onSettings, type Settings } from '../meta/settings';

/**
 * Procedural soundtrack (D121): no audio files. A low, slowly breathing drone
 * plus sparse plucked notes from a minor scale; when danger rises (a horde is
 * coming or fighting at the walls) a pulse joins and the notes come faster.
 * Starts on the first user gesture, follows the Music / volume settings.
 */
const SCALE = [0, 3, 5, 7, 10, 12, 15]; // minor pentatonic + octave
const ROOT = 55; // A1

export class Music {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private drone: { a: OscillatorNode; b: OscillatorNode; filter: BiquadFilterNode } | null = null;
  private pulse: GainNode | null = null;
  private timer = 0;
  intensity = 0;
  private seed = 1;
  private settings: Settings = loadSettings();
  private readonly off: () => void;
  private readonly unlock = (): void => this.start();
  /** Notes played (debug / tests). */
  notes = 0;

  constructor() {
    this.off = onSettings((s) => {
      this.settings = s;
      this.applyVolume();
    });
    window.addEventListener('pointerdown', this.unlock, { passive: true });
    window.addEventListener('keydown', this.unlock);
  }

  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  /** 0 = calm … 1 = a horde at the walls. */
  setIntensity(v: number): void {
    this.intensity = Math.max(0, Math.min(1, v));
    if (this.drone && this.ctx) this.drone.filter.frequency.setTargetAtTime(220 + 520 * this.intensity, this.ctx.currentTime, 1.5);
    if (this.pulse && this.ctx) this.pulse.gain.setTargetAtTime(this.intensity > 0.4 ? 0.12 * this.intensity : 0, this.ctx.currentTime, 0.8);
  }

  destroy(): void {
    this.off();
    window.removeEventListener('pointerdown', this.unlock);
    window.removeEventListener('keydown', this.unlock);
    window.clearTimeout(this.timer);
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }

  private applyVolume(): void {
    if (!this.master || !this.ctx) return;
    const on = this.settings.music && this.settings.sound;
    this.master.gain.setTargetAtTime(on ? this.settings.musicVolume * 0.35 : 0, this.ctx.currentTime, 0.3);
  }

  private start(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => undefined);
      return;
    }
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    try {
      this.ctx = new AC();
    } catch {
      return;
    }
    const ctx = this.ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(ctx.destination);
    // Drone: two detuned saws through a soft low-pass.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 220;
    filter.Q.value = 0.7;
    const dg = ctx.createGain();
    dg.gain.value = 0.18;
    const a = ctx.createOscillator();
    const b = ctx.createOscillator();
    a.type = b.type = 'sawtooth';
    a.frequency.value = ROOT;
    b.frequency.value = ROOT * 1.5 * 1.003;
    a.connect(filter);
    b.connect(filter);
    filter.connect(dg).connect(this.master);
    // A slow "breath" on the drone.
    const lfo = ctx.createOscillator();
    const lg = ctx.createGain();
    lfo.frequency.value = 0.07;
    lg.gain.value = 0.06;
    lfo.connect(lg).connect(dg.gain);
    // Danger pulse: a low thump at ~100 bpm, silent until intensity rises.
    const pulseOsc = ctx.createOscillator();
    pulseOsc.type = 'sine';
    pulseOsc.frequency.value = ROOT;
    const pulseAmp = ctx.createGain();
    pulseAmp.gain.value = 0;
    const pulseLfo = ctx.createOscillator();
    pulseLfo.type = 'square';
    pulseLfo.frequency.value = 1.66;
    const pulseDepth = ctx.createGain();
    pulseDepth.gain.value = 0.5;
    pulseLfo.connect(pulseDepth).connect(pulseAmp.gain);
    this.pulse = ctx.createGain();
    this.pulse.gain.value = 0;
    pulseOsc.connect(pulseAmp).connect(this.pulse).connect(this.master);
    for (const o of [a, b, lfo, pulseOsc, pulseLfo]) o.start();
    this.drone = { a, b, filter };
    this.applyVolume();
    this.schedule();
  }

  private rand(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return this.seed / 2147483647;
  }

  private schedule(): void {
    const wait = (3200 - 2200 * this.intensity) * (0.6 + this.rand() * 0.8);
    this.timer = window.setTimeout(() => {
      this.pluck();
      this.schedule();
    }, wait);
  }

  private pluck(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || ctx.state !== 'running') return;
    const step = SCALE[Math.floor(this.rand() * SCALE.length)]!;
    const f = ROOT * 4 * 2 ** (step / 12);
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.value = f;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 1.9);
    this.notes++;
  }
}
